import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { AuthContext } from "../auth/AuthContext";
import mark1 from "../assets/img/users.png";

export default function ProfileLockModal() {
  const { profile, token, setProfile, setProfileIncomplete, logout } =
    useContext(AuthContext);

  const baseURL = process.env.REACT_APP_API_URL;

  const [form, setForm] = useState({
    username: "",
    fullname: "",
    email: "",
    mobile_no: "",
  });

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(mark1);
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!profile) return;

    setForm({
      username: profile.username || "",
      fullname: profile.fullname || "",
      email: profile.email || "",
      mobile_no: profile.mobile_no || "",
    });

    setPreview(
      profile.profile_pic
        ? `${baseURL}/uploads/${profile.profile_pic}?t=${Date.now()}`
        : mark1
    );
  }, [profile, baseURL]);

  // lock scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // cleanup object URL
  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  const requiredMissing = useMemo(() => {
    const u = form.username.trim();
    const f = form.fullname.trim();
    const e = form.email.trim();
    const m = form.mobile_no.trim();

    const hasPic = Boolean(profile?.profile_pic) || Boolean(file);
    return !(u && f && e && m && hasPic);
  }, [form, file, profile]);

  const handlePickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (!f.type.startsWith("image/")) {
      Swal.fire("Error", "Only image files allowed", "error");
      return;
    }

    if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);

    const url = URL.createObjectURL(f);
    setLocalPreviewUrl(url);
    setPreview(url); // ✅ instant preview
    setFile(f);
  };

  const handleLogoutClick = async () => {
    const ok = await Swal.fire({
      title: "Logout?",
      text: "You will be logged out now.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Logout",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });

    if (ok.isConfirmed) logout();
  };

  const handleSave = async () => {
    if (requiredMissing) {
      Swal.fire(
        "Required",
        "Please fill all fields and upload profile picture.",
        "warning"
      );
      return;
    }

    // basic email + mobile check (optional but helpful)
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      Swal.fire("Invalid", "Please enter a valid email.", "warning");
      return;
    }
    if (!/^\d{10,15}$/.test(form.mobile_no.trim())) {
      Swal.fire("Invalid", "Please enter a valid mobile number.", "warning");
      return;
    }

    try {
      setSaving(true);

      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      if (file) fd.append("profile_pic", file);

      const res = await axios.put(`${baseURL}/dashboard/profile-update`, fd, {
        headers: {
          "x-access-token": token,
          "Content-Type": "multipart/form-data",
        },
      });

      setProfile(res.data.result);
      setProfileIncomplete(false); // ✅ unlock

      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
      setLocalPreviewUrl(null);
      setFile(null);

      Swal.fire("Done", "Profile completed successfully!", "success");
    } catch (e) {
      Swal.fire("Error", e.response?.data?.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.headRow}>
            <h4 style={{ margin: 0, fontWeight: 800 }}>Complete Your Profile</h4>

            {/* ✅ Logout button */}
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={handleLogoutClick}
              disabled={saving}
              style={{ fontWeight: 800 }}
            >
              <i className="fa fa-sign-out me-1" /> Logout
            </button>
          </div>

          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
            You won’t be able to access the dashboard until you complete your
            profile.
          </div>
        </div>

        <div style={styles.body}>
          <div
            style={{
              display: "flex",
              gap: 14,
              alignItems: "center",
              marginBottom: 18,
            }}
          >
            <img
              src={preview}
              alt="Profile"
              style={{
                width: 78,
                height: 78,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid rgba(187,17,20,0.25)",
              }}
            />

            <div>
              <label className="btn btn-outline-secondary btn-sm">
                Upload Photo <span className="text-danger">*</span>
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handlePickFile}
                />
              </label>
              <div style={{ fontSize: 12, marginTop: 6, opacity: 0.7 }}>
                Profile picture required.
              </div>
            </div>
          </div>

          {["username", "fullname", "email", "mobile_no"].map((f) => (
            <div className="mb-3" key={f}>
              <label
                className="text-capitalize text-dark"
                style={{ fontWeight: 700 }}
              >
                {f.replace("_", " ")} <span className="text-danger">*</span>
              </label>

              <input
                className="form-control"
                value={form[f]}
                onChange={(e) => setForm({ ...form, [f]: e.target.value })}
              />
            </div>
          ))}

          <button
            className="btn btn-success w-100"
            onClick={handleSave}
            disabled={saving}
            style={{ fontWeight: 800 }}
          >
            {saving ? "Saving..." : "Save & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.78)",
    zIndex: 101,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  card: {
    width: "100%",
    maxWidth: 560,
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
    overflow: "hidden",
  },
  header: {
    padding: 18,
    borderBottom: "1px solid rgba(0,0,0,0.08)",
  },
  headRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  body: { padding: 18 },
};
